import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, AlertCircle } from 'lucide-react';

const roleHome = (role) => (role === 'doctor' ? '/doctor' : role === 'nurse' ? '/nurse' : '/admin');

export default function InviteAccept() {
  const { token } = useParams();
  const { acceptInvite } = useApp();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', specialty: '', npi: '', license_number: '' });

  useEffect(() => {
    api.get(`/invites/token/${token}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, name: r.data.invitee_name || '', email: r.data.email || '' }));
      })
      .catch((e) => setError(errText(e, 'This invite link is invalid or has expired.')));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 10 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) { toast.error('Password must be at least 10 characters and include a letter and a number'); return; }
    setBusy(true);
    try {
      const data = await acceptInvite(token, form);
      toast.success(`Welcome to ${data?.practice?.name || 'the practice'}!`);
      navigate(roleHome(invite.role), { replace: true });
    } catch (err) {
      toast.error(errText(err, 'Could not accept invite.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-wash">
      <div className="noise min-h-screen flex flex-col">
        <header className="h-14 flex items-center px-4 lg:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="font-semibold tracking-tight">DoctorPaperwork</span>
          </Link>
        </header>
        <div className="flex-1 grid place-items-center px-4 py-10">
          <Card className="w-full max-w-md shadow-lg shadow-black/5" data-testid="invite-accept-card">
            {error ? (
              <CardContent className="p-8">
                <div className="flex items-start gap-3 text-sm" data-testid="invite-error">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium">Invite unavailable</p>
                    <p className="text-muted-foreground mt-1">{error}</p>
                    <Button variant="outline" size="sm" className="mt-4" asChild><Link to="/login">Go to sign in</Link></Button>
                  </div>
                </div>
              </CardContent>
            ) : !invite ? (
              <CardContent className="p-8 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></CardContent>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="text-xl">Join {invite.practice_name}</CardTitle>
                  <CardDescription>
                    You have been invited as <Badge variant="secondary" className="capitalize ml-1" data-testid="invite-role-badge">{invite.role}</Badge>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Full name</Label>
                      <Input required value={form.name} onChange={set('name')} placeholder={invite.role === 'doctor' ? 'Dr. Alex Morgan, MD' : 'Alex Morgan'} data-testid="invite-name-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Work email</Label>
                      <Input required type="email" value={form.email} onChange={set('email')} placeholder="you@yourpractice.com" data-testid="invite-email-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Password</Label>
                      <Input required type="password" value={form.password} onChange={set('password')} placeholder="10+ characters, letter + number" data-testid="invite-password-input" />
                    </div>
                    {invite.role === 'doctor' && (
                      <div className="rounded-lg border bg-secondary/50 p-3 space-y-3">
                        <p className="text-xs text-muted-foreground">Provider credentials (appear on signed documents)</p>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">Specialty</Label>
                          <Input value={form.specialty} onChange={set('specialty')} placeholder="Orthopedic Surgery" data-testid="invite-specialty-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">NPI</Label>
                            <Input value={form.npi} onChange={set('npi')} placeholder="1234567890" data-testid="invite-npi-input" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">License #</Label>
                            <Input value={form.license_number} onChange={set('license_number')} placeholder="MD-987654" data-testid="invite-license-input" />
                          </div>
                        </div>
                      </div>
                    )}
                    <Button type="submit" className="w-full" disabled={busy} data-testid="invite-accept-submit">
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account & join
                    </Button>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
